import React, { useRef } from 'react';

export interface QrPlaceholderValue {
  x: number;
  y: number;
  width: number;
  height: number;
  safeMargin: number;
}

interface Props {
  imageUrl: string;
  value: QrPlaceholderValue;
  onChange: (v: QrPlaceholderValue) => void;
}

type DragMode = 'move' | 'resize' | null;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

/**
 * Lets an admin position a percentage-based QR placeholder rectangle over the
 * uploaded template preview by dragging the box (move) or its corner handle
 * (resize). Numeric inputs allow precise values. No extra dependencies — pure
 * pointer events + percentages so placement is resolution-independent.
 */
const QrPlaceholderEditor: React.FC<Props> = ({ imageUrl, value, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const modeRef = useRef<DragMode>(null);
  const startRef = useRef<{
    mx: number;
    my: number;
    box: QrPlaceholderValue;
  } | null>(null);

  const beginDrag = (mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    modeRef.current = mode;
    startRef.current = { mx: e.clientX, my: e.clientY, box: { ...value } };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!modeRef.current || !startRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - startRef.current.mx) / rect.width) * 100;
    const dy = ((e.clientY - startRef.current.my) / rect.height) * 100;
    const b = startRef.current.box;

    if (modeRef.current === 'move') {
      onChange({
        ...b,
        x: clamp(b.x + dx, 0, 100 - b.width),
        y: clamp(b.y + dy, 0, 100 - b.height),
      });
    } else {
      onChange({
        ...b,
        width: clamp(b.width + dx, 5, 100 - b.x),
        height: clamp(b.height + dy, 5, 100 - b.y),
      });
    }
  };

  const endDrag = () => {
    modeRef.current = null;
    startRef.current = null;
  };

  const setField = (k: keyof QrPlaceholderValue, raw: string) => {
    const max = k === 'safeMargin' ? 50 : 100;
    onChange({ ...value, [k]: clamp(parseFloat(raw) || 0, 0, max) });
  };

  const fields: { key: keyof QrPlaceholderValue; label: string }[] = [
    { key: 'x', label: 'X' },
    { key: 'y', label: 'Y' },
    { key: 'width', label: 'Width' },
    { key: 'height', label: 'Height' },
    { key: 'safeMargin', label: 'Margin' },
  ];

  return (
    <div>
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 select-none touch-none"
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          src={imageUrl}
          alt="Template preview"
          className="block w-full h-auto pointer-events-none"
          draggable={false}
        />

        <div
          className="absolute border-2 border-primary-500 bg-primary-500/20 cursor-move"
          style={{
            left: `${value.x}%`,
            top: `${value.y}%`,
            width: `${value.width}%`,
            height: `${value.height}%`,
          }}
          onPointerDown={beginDrag('move')}
        >
          {value.safeMargin > 0 && (
            <div
              className="absolute border border-dashed border-primary-300 pointer-events-none"
              style={{
                left: `${value.safeMargin}%`,
                top: `${value.safeMargin}%`,
                right: `${value.safeMargin}%`,
                bottom: `${value.safeMargin}%`,
              }}
            />
          )}

          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-primary-700 dark:text-primary-100 pointer-events-none">
            QR
          </span>

          <div
            className="absolute -right-1.5 -bottom-1.5 h-3.5 w-3.5 rounded-sm bg-primary-600 border border-white cursor-nwse-resize"
            onPointerDown={beginDrag('resize')}
          />
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2 mt-3">
        {fields.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
              {label} (%)
            </label>
            <input
              type="number"
              min={0}
              max={key === 'safeMargin' ? 50 : 100}
              step="any"
              value={Math.round((value[key] ?? 0) * 10) / 10}
              onChange={(e) => setField(key, e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Drag the box to position the QR code; drag the corner handle to resize.
        Margin keeps a quiet zone around the code. All values are percentages of
        the template.
      </p>
    </div>
  );
};

export default QrPlaceholderEditor;
